import { Bookmark, BookmarkCheck, Play } from 'lucide-react';

import { t } from '../i18n';
import type { Episode, EpisodeCollectionType } from '../types/bgm';
import { Button } from './button';

const localDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const hasEpisodeAired = (airdate: string | undefined): boolean =>
  Boolean(airdate && /^\d{4}-\d{2}-\d{2}$/.test(airdate) && airdate <= localDate());

export interface EpisodeListProps {
  activeEpisodeId?: number;
  episodeStatuses?: Record<number, EpisodeCollectionType>;
  episodes: Episode[];
  onPlay: (episode: Episode) => void;
  onToggleWatchLater?: (episode: Episode) => void;
  onToggleSelection?: (episodeId: number, selected: boolean) => void;
  selectedEpisodeIds?: ReadonlySet<number>;
  selectionMode?: boolean;
  watchLaterEpisodeIds?: ReadonlySet<number>;
  watchLaterUpdatingEpisodeIds?: ReadonlySet<number>;
}

export const EpisodeList = ({
  activeEpisodeId,
  episodeStatuses = {},
  episodes,
  onPlay,
  onToggleWatchLater,
  onToggleSelection,
  selectedEpisodeIds = new Set<number>(),
  selectionMode = false,
  watchLaterEpisodeIds = new Set<number>(),
  watchLaterUpdatingEpisodeIds = new Set<number>(),
}: EpisodeListProps) => {
  const statusLabels: Record<EpisodeCollectionType, string> = {
    0: t('episodeUnwatched'),
    1: t('episodeWish'),
    2: t('episodeWatched'),
    3: t('episodeDropped'),
  };

  return (
    <div className={`episode-grid ${selectionMode ? 'selecting' : ''}`.trim()}>
      {episodes.map((episode) => {
        const aired = hasEpisodeAired(episode.airdate);
        const selected = selectedEpisodeIds.has(episode.id);
        const status = episodeStatuses[episode.id] ?? 0;
        const savedForLater = watchLaterEpisodeIds.has(episode.id);
        return (
          <div
            className={`episode-row ${aired ? 'aired' : 'upcoming'} ${selectionMode ? 'selecting' : ''} ${selected ? 'selected' : ''} ${activeEpisodeId === episode.id ? 'active' : ''}`.trim()}
            key={episode.id}
            onClick={(event) => {
              if (selectionMode && !(event.target instanceof HTMLInputElement)) {
                onToggleSelection?.(episode.id, !selected);
              }
            }}
          >
            {selectionMode ? (
              <input
                className="episode-checkbox"
                type="checkbox"
                checked={selected}
                onChange={(event) => onToggleSelection?.(episode.id, event.currentTarget.checked)}
              />
            ) : null}
            <span>{episode.sort}</span>
            <div>
              <strong>{episode.name_cn || episode.name || `EP.${episode.sort}`}</strong>
              <div className="episode-meta">
                <small>{[episode.airdate, episode.duration].filter(Boolean).join(' · ') || '-'}</small>
                <span className="episode-statuses">
                  <span className={`episode-collection-status status-${status}`}>{statusLabels[status]}</span>
                  <span className="episode-status">{t(aired ? 'episodeAired' : 'episodeUpcoming')}</span>
                </span>
              </div>
            </div>
            {!selectionMode ? (
              <div className="episode-row-actions flex items-center gap-1.5">
                {onToggleWatchLater ? (
                  <Button
                    className={`episode-play-button ${savedForLater ? 'text-(--input-accent)!' : ''}`.trim()}
                    compact
                    iconOnly
                    aria-label={t(savedForLater ? 'removeWatchLater' : 'addWatchLater')}
                    title={t(savedForLater ? 'removeWatchLater' : 'addWatchLater')}
                    loading={watchLaterUpdatingEpisodeIds.has(episode.id)}
                    onClick={() => onToggleWatchLater(episode)}
                  >
                    {savedForLater ? <BookmarkCheck className="size-3.5!" /> : <Bookmark className="size-3.5!" />}
                  </Button>
                ) : null}
                <Button
                  className="episode-play-button"
                  compact
                  iconOnly
                  aria-label={`${t('playMediaResource')}: ${episode.name_cn || episode.name || `EP.${episode.sort}`}`}
                  title={t('playMediaResource')}
                  onClick={() => onPlay(episode)}
                >
                  <Play className="size-3.5!" />
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
